<template>
  <div id="adminSessionExportModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header py-2">
          <h5 class="modal-title">Export Smart Search 2.0 Sessions</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="exporting"
            @click="onCancel"
          ></button>
        </div>
        <div class="modal-body py-3">
          <div class="export-form">
            <p class="text-muted small mb-2">
              One row per turn. List model/search filters do not apply.
            </p>

            <div class="row g-2">
              <div class="col-12">
                <label class="form-label form-label-sm fw-bold mb-1">
                  Customer <span class="text-danger">*</span>
                </label>
                <select
                  v-model="customerId"
                  class="form-select form-select-sm"
                  :disabled="exporting"
                  @change="onCustomerChange"
                >
                  <option value="">Select a customer...</option>
                  <option
                    v-for="customer in customers"
                    :key="customer.customerId"
                    :value="customer.customerId"
                  >
                    {{ customer.customerName }}
                  </option>
                </select>
              </div>

              <div class="col-sm-7 export-inline-field">
                <label class="form-label form-label-sm fw-bold mb-1">Date range</label>
                <Datepicker
                  v-model="dates"
                  :range="true"
                  :clearable="true"
                  :enableTimePicker="false"
                  :disabled="exporting"
                  :min-date="minExportDate"
                  :preventMinMaxNavigation="true"
                  class="export-date-picker"
                  placeholder="All time"
                  @update:model-value="onDatesChanged"
                />
                <div class="form-text">Optional. From Jul 16, 2026.</div>
              </div>

              <div class="col-12">
                <label class="form-label form-label-sm fw-bold mb-1">Users</label>
                <div :class="{ 'pe-none opacity-50': exporting || !customerId }">
                  <select-dropdown
                    :list="userOptions"
                    label-field="exportLabel"
                    list-title="Users"
                    @update:selectedItems="onUsersSelected"
                  />
                </div>
                <div class="form-text">Optional. None selected exports all users at this customer.</div>
              </div>

              <div class="col-sm-6">
                <label class="form-label form-label-sm fw-bold mb-1">Initiation type</label>
                <select
                  v-model="initiationType"
                  class="form-select form-select-sm"
                  :disabled="exporting"
                >
                  <option value="">All types</option>
                  <option value="SMART_SEARCH">Smart Search</option>
                  <option value="MANUAL_FILTERS">Manual Filters</option>
                </select>
              </div>

              <div class="col-sm-6">
                <label class="form-label form-label-sm fw-bold mb-1">Min user turns</label>
                <input
                  v-model.number="minUserTurns"
                  type="number"
                  min="1"
                  step="1"
                  class="form-control form-control-sm"
                  :disabled="exporting"
                />
                <div class="form-text">Use 2+ for return visits.</div>
              </div>

              <div class="col-12">
                <div class="form-check mb-0">
                  <input
                    id="nonAdminOnly"
                    v-model="nonAdminOnly"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="exporting"
                  />
                  <label class="form-check-label small" for="nonAdminOnly">
                    Non-admin users only
                  </label>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger py-2 mt-2 mb-0 small" role="alert">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <div class="modal-footer py-2">
          <button type="button" class="btn btn-secondary" :disabled="exporting" @click="onCancel">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" :disabled="!canSubmit" @click="onExport">
            <span v-if="exporting">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Exporting...
            </span>
            <span v-else>Export CSV</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vue3-date-time-picker';
import 'vue3-date-time-picker/dist/main.css';
import SelectDropdown from './SelectDropdown.vue';
import InsightsService from '../services/InsightsService.js';
import AppUtil from '../utils/AppUtil.js';
import {
  buildSs2ExportFilename,
  ss2ExportMinDate,
} from '../data/ss2Export.js';

export default {
  name: 'AdminSessionExportModal',
  components: {
    Datepicker,
    SelectDropdown,
  },
  props: {
    prefill: {
      type: Object,
      required: true,
    },
  },
  emits: ['closed'],
  data() {
    return {
      model: streetmetrics.model,
      customerId: '',
      dates: [],
      startDate: null,
      endDate: null,
      userOptions: [],
      selectedUserIds: [],
      nonAdminOnly: true,
      initiationType: '',
      minUserTurns: 1,
      exporting: false,
      errorMessage: null,
      currentModal: null,
      modalElement: null,
      insightsService: new InsightsService(),
      appUtil: new AppUtil(),
      minExportDate: ss2ExportMinDate(),
    };
  },
  computed: {
    customers() {
      return this.model.customers || [];
    },
    customerUsers() {
      if (!this.customerId) {
        return [];
      }
      return (this.model.users || [])
        .filter((user) => user.customer?.customerId == this.customerId)
        .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
    },
    canSubmit() {
      return Boolean(this.customerId) && !this.exporting;
    },
  },
  mounted() {
    this.applyPrefill();
    this.modalElement = document.getElementById('adminSessionExportModal');
    this.currentModal = new bootstrap.Modal(this.modalElement, {
      backdrop: this.exporting ? 'static' : true,
    });

    this.modalElement.addEventListener('hidden.bs.modal', this.onModalHidden);
    this.currentModal.show();
  },
  beforeUnmount() {
    if (this.modalElement) {
      this.modalElement.removeEventListener('hidden.bs.modal', this.onModalHidden);
    }
  },
  methods: {
    applyPrefill() {
      const { filters = {}, startDate, endDate, dateFilter } = this.prefill;

      this.customerId = filters.customerId || '';
      this.initiationType = filters.initiationType || '';
      this.nonAdminOnly = true;

      if (filters.userId && filters.userId !== 'non-admin') {
        this.rebuildUserOptions([Number(filters.userId)]);
      } else {
        this.rebuildUserOptions([]);
      }

      if (dateFilter && dateFilter !== 'all' && startDate && endDate) {
        const minDate = ss2ExportMinDate();
        let clampedStart = new Date(startDate);
        const clampedEnd = new Date(endDate);
        if (clampedStart < minDate) {
          clampedStart = minDate;
        }
        this.startDate = clampedStart;
        this.endDate = clampedEnd;
        this.dates = [clampedStart, clampedEnd];
      }
    },
    onCustomerChange() {
      this.rebuildUserOptions([]);
    },
    rebuildUserOptions(selectedUserIds = []) {
      const selectedSet = new Set(selectedUserIds.map((id) => Number(id)));
      this.userOptions = this.customerUsers.map((user) => ({
        ...user,
        exportLabel: user.displayName || String(user.userId),
        selected: selectedSet.has(Number(user.userId)),
      }));
      this.selectedUserIds = this.userOptions
        .filter((user) => user.selected)
        .map((user) => user.userId);
    },
    onUsersSelected(selectedUsers) {
      this.selectedUserIds = selectedUsers.map((user) => user.userId);
    },
    onDatesChanged(nextDates) {
      if (!nextDates || nextDates.length !== 2 || !nextDates[0] || !nextDates[1]) {
        this.dates = [];
        this.startDate = null;
        this.endDate = null;
        return;
      }

      const minDate = ss2ExportMinDate();
      let clampedStart = new Date(nextDates[0]);
      const clampedEnd = new Date(nextDates[1]);
      if (clampedStart < minDate) {
        clampedStart = minDate;
      }

      this.dates = [clampedStart, clampedEnd];
      this.startDate = clampedStart;
      this.endDate = clampedEnd;
    },
    buildExportParams() {
      const params = {
        customerId: Number(this.customerId),
        includeInternalUsers: !this.nonAdminOnly,
      };

      if (this.startDate && this.endDate) {
        const formatted = this.appUtil.formatStartAndEndDates(this.startDate, this.endDate);
        params.startDate = formatted.start;
        params.endDate = formatted.end;
      }

      if (this.selectedUserIds.length) {
        params.userIds = this.selectedUserIds.map((userId) => Number(userId));
      }

      if (this.initiationType) {
        params.initiationType = this.initiationType;
      }

      const minUserTurns = Number(this.minUserTurns);
      if (Number.isFinite(minUserTurns) && minUserTurns > 1) {
        params.minUserTurns = minUserTurns;
      }

      return params;
    },
    async onExport() {
      if (!this.canSubmit) {
        return;
      }

      this.exporting = true;
      this.errorMessage = null;

      try {
        const params = this.buildExportParams();
        const result = await this.insightsService.exportAdminSessions(params);
        const turnCount = Number(result?.turnCount) || 0;
        const sessionCount = Number(result?.sessionCount) || 0;

        if (turnCount === 0) {
          this.toast('No turns matched these filters.', 'bg-warning');
          return;
        }

        const filename = buildSs2ExportFilename(this.customerId, this.customers);
        this.appUtil.downloadCSV(result.csv, filename);
        this.toast(
          `Exported ${turnCount.toLocaleString()} turns from ${sessionCount.toLocaleString()} sessions.`,
        );
        this.hideModal();
      } catch (error) {
        const rawMessage = error?.message || error?.error?.message;
        this.errorMessage = Array.isArray(rawMessage)
          ? rawMessage.join(', ')
          : rawMessage || 'Failed to export sessions. Please try again.';
      } finally {
        this.exporting = false;
      }
    },
    toast(message, background = 'bg-success') {
      this.model.toast.message = null;
      this.$nextTick(() => {
        this.model.toast.message = message;
        this.model.toast.background = background;
      });
    },
    onCancel() {
      if (this.exporting) {
        return;
      }
      this.hideModal();
    },
    hideModal() {
      if (this.currentModal) {
        this.currentModal.hide();
      } else {
        this.$emit('closed');
      }
    },
    onModalHidden() {
      this.$emit('closed');
    },
  },
};
</script>

<style scoped>
.export-form {
  max-width: 28rem;
}

.form-label-sm {
  font-size: 0.8125rem;
}

.export-date-picker {
  width: 100%;
}

.export-date-picker :deep(input) {
  height: 2.125rem;
  min-height: 2.125rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.export-date-picker :deep(.dp__input_wrap),
.export-date-picker :deep(.dp__main) {
  width: 100%;
  min-height: 2.125rem;
}

.export-inline-field .form-text {
  margin-bottom: 0;
}
</style>
